import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, signal } from '@angular/core';
import { AccountController } from '@app/api/controller/models/account-controller.model';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { CcrControllerOutOfSync } from '@app/common/services/models/cloud-connect/ccr-controller-out-of-sync.model';
import { CcrControllers } from '@app/common/services/models/cloud-connect/ccr-controllers.model';
import { CcrEtDissemination } from '@app/common/services/models/cloud-connect/ccr-et-dissemination.model';
import { CcrGetAlerts } from '@app/common/services/models/cloud-connect/ccr-get-alerts.model';
import { ControllerManagerService } from '@app/api/controller/controller-manager.service';
import { isShownOutOfSync } from '@app/shared/utils/controller-sync.util';
import { navigateToOutOfSync } from '@app/shared/utils/out-of-sync-navigation';
import { Router } from '@angular/router';
import { catchError, debounceTime, filter, of, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { NgOptimizedImage } from '@angular/common';
import { SkeletonComponent } from '@app/shared/skeleton/skeleton.component';
import { SkeletonRegionComponent } from '@app/shared/skeleton/skeleton-region/skeleton-region.component';

@Component({
    selector: 'ic-toolbar-alerts',
    templateUrl: './toolbar-alerts.component.html',
    styleUrls: ['./toolbar-alerts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgOptimizedImage, SkeletonComponent, SkeletonRegionComponent, Tooltip, TranslatePipe]
})
export class ToolbarAlertsComponent {
    /** One re-read of the alarm counts per burst of alarm messages; a controller sends several at once. */
    static readonly ALERT_COUNT_REFRESH_MS = 1000;

    private readonly destroyRef = inject(DestroyRef);

    private broadcastService = inject(BroadcastService);
    private controllerManager = inject(ControllerManagerService);
    private router = inject(Router);

    private _accountId: string = '';
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set accountId(value: string) {
        // Empty as well as null (AB#24169) — parent binds '' before the real id arrives.
        if (!value) { return; }

        this._accountId = value;
        this.retrieveAccountAlarms();
    }

    get accountId() {
        return this._accountId;
    }

    // Signals, so the badges update from the API and AppSync callbacks without zone.js (ADR 0008). '--' is the error
    // value; while the controllers load the badges show skeleton counts (ER-1543).
    protected readonly alarmsCount = signal('--');
    protected readonly etFailureCount = signal('--');
    protected readonly outOfSyncCount = signal('--');
    protected readonly isLoading = signal(true);

    private accountControllers: AccountController[] = [];

    // =========================================================================================================================================================
    // Constructor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor() {
        // Update Out of Sync Counts (as appropriate)
        this.broadcastService.cc_controllerOutOfSync
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((info: CcrControllerOutOfSync) => {
                const controller = this.accountControllers.find(c => (info.cloudID != null && c.cloudID == info.cloudID) || c.id == info.controllerID);
                if (controller != null) {
                    controller.syncStatus = info.syncStatus;
                    this.setIndicatorCounts();
                }
            });

        this.broadcastService.cc_etDissemination
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((info: CcrEtDissemination) => {
                const controller = this.accountControllers.find(c => (info.cloudID != null && c.cloudID == info.cloudID) || c.id == info.controllerID);
                if (controller != null) {
                    controller.hasEtDisseminationFailure = info.etDisseminationStatus;
                    this.setIndicatorCounts();
                }
            });

        // Read the saved count back rather than adding the message's count: an alarm message re-sends every active
        // alarm, so adding only ever grew it (AB#21734). Same rule as the home page's Alarms card.
        this.broadcastService.cc_getAlerts
            .pipe(
                filter((info: CcrGetAlerts) => this.accountControllers.some(c => c.cloudID == info.cloudID)),
                debounceTime(ToolbarAlertsComponent.ALERT_COUNT_REFRESH_MS),
                switchMap(() => this.controllerManager.getAccountControllers(true).pipe(catchError(() => of(null)))),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((fresh: AccountController[] | null) => {
                if (!fresh) {
                    return;
                }
                for (const controller of this.accountControllers) {
                    const latest = fresh.find(c => c.id === controller.id);
                    if (latest) {
                        controller.alertCount = latest.alertCount;
                    }
                }
                this.setIndicatorCounts();
            });

        // A controller that goes offline leaves the Out of Sync badge. The sync page uses the same rule.
        this.broadcastService.cc_controllersInformation
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: CcrControllers) => {
                let changed = false;
                (response.controllers ?? []).forEach(ctl => {
                    const controller = this.accountControllers.find(c => c.cloudID == ctl.cloudID);
                    const online = ctl.online ?? false;
                    if (controller != null && controller.isOnline !== online) {
                        controller.isOnline = online;
                        changed = true;
                    }
                });
                if (changed) { this.setIndicatorCounts(); }
            });
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private retrieveAccountAlarms(): void {
        if (!this.accountId) { return; }

        this.isLoading.set(true);
        this.controllerManager.getAccountControllers(false, this.accountId)
            .pipe(take(1))
            .subscribe({
                next: (controllers: AccountController[]) => {
                    this.accountControllers = controllers;
                    this.setIndicatorCounts();
                    this.isLoading.set(false);
                },
                // Not fatal: the badges keep '--', but the skeleton must end (ER-1543).
                error: () => this.isLoading.set(false)
            });
    }

    private setIndicatorCounts() {
        this.outOfSyncCount.set(this.accountControllers.filter(c => isShownOutOfSync(c)).length.toString());
        this.alarmsCount.set(this.accountControllers.filter(c => c.alertCount > 0).length.toString());
        this.etFailureCount.set(this.accountControllers.filter(c => c.hasEtDisseminationFailure).length.toString());
    }

    protected goToOutOfSync(): void {
        navigateToOutOfSync(this.router);
    }

}
